import { MainMenu } from './MainMenu.js';
import { LevelSelect } from './LevelSelect.js';
import { HUD } from './HUD.js';
import { WinOverlay } from './WinOverlay.js';

export const UIState = {
  MAIN_MENU: 'main_menu',
  LEVEL_SELECT: 'level_select',
  PLAYING: 'playing',
  WIN: 'win',
};

export class UIManager {
  /**
   * @param {HTMLElement} root
   * @param {import('../storage/ProgressStore.js').ProgressStore} store
   * @param {object} handlers
   * @param {(levelId: number) => Promise<boolean>} handlers.onStartLevel
   * @param {() => void} handlers.onRestart
   * @param {() => void} handlers.onExit
   * @param {() => number} handlers.getElapsedMs
   * @param {() => Promise<{ok:boolean, reason?:string}>} [handlers.onToggleOrientation]
   * @param {() => void} [handlers.onCalibrateOrientation]
   * @param {() => boolean} [handlers.isOrientationEnabled]
   * @param {() => boolean} [handlers.isOrientationSupported]
   */
  constructor(root, store, {
    onStartLevel,
    onRestart,
    onExit,
    getElapsedMs,
    onToggleOrientation,
    onCalibrateOrientation,
    isOrientationEnabled,
    isOrientationSupported,
  }) {
    this.root = root;
    this.store = store;
    this.onStartLevel = onStartLevel;
    this.onRestart = onRestart;
    this.onExit = onExit;
    this.getElapsedMs = getElapsedMs;
    this.onToggleOrientation = onToggleOrientation || (async () => ({ ok: true }));
    this.onCalibrateOrientation = onCalibrateOrientation || (() => {});
    this.isOrientationEnabled = isOrientationEnabled || (() => false);
    this.isOrientationSupported = isOrientationSupported || (() => false);

    this.state = null;
    this.currentLevelId = null;

    this.mainMenu = new MainMenu(root, {
      onStart: () => this._startDefaultLevel(),
      onLevelSelect: () => this.setState(UIState.LEVEL_SELECT),
    });
    this.levelSelect = new LevelSelect(root, store, {
      onPick: (id) => this._goToLevel(id),
      onBack: () => this.setState(UIState.MAIN_MENU),
    });
    this.hud = new HUD(root, {
      onRestart: () => this.onRestart(),
      onMenu: () => {
        this.onExit();
        this.setState(UIState.LEVEL_SELECT);
      },
      onToggleOrientation: async () => {
        const res = await this.onToggleOrientation();
        if (!res.ok && res.reason) this.showError(res.reason);
        this.hud.refreshGyroState();
      },
      onCalibrate: () => this.onCalibrateOrientation(),
      isOrientationEnabled: () => this.isOrientationEnabled(),
      isOrientationSupported: () => this.isOrientationSupported(),
    });
    this.winOverlay = new WinOverlay(root, store, {
      onNext: (nextId) => this._goToLevel(nextId),
      onReplay: () => this._goToLevel(this.currentLevelId),
      onLevelSelect: () => {
        this.onExit();
        this.setState(UIState.LEVEL_SELECT);
      },
      onMainMenu: () => {
        this.onExit();
        this.setState(UIState.MAIN_MENU);
      },
    });

    this._tickHandle = null;
  }

  setState(next) {
    this.mainMenu.hide();
    this.levelSelect.hide();
    this.hud.hide();
    this.winOverlay.hide();
    this.state = next;
    if (next === UIState.MAIN_MENU) this.mainMenu.show();
    else if (next === UIState.LEVEL_SELECT) this.levelSelect.show();
    else if (next === UIState.PLAYING) {
      this.hud.show(this.currentLevelId);
      this._startTicker();
    } else if (next === UIState.WIN) {
      this.hud.show(this.currentLevelId);
    }
    if (next !== UIState.PLAYING && next !== UIState.WIN) this._stopTicker();
  }

  async _startDefaultLevel() {
    const last = this.store.getLastPlayedLevel();
    await this._goToLevel(last);
  }

  async _goToLevel(levelId) {
    this.currentLevelId = levelId;
    this.store.setLastPlayedLevel(levelId);
    this.setState(UIState.PLAYING);
    const ok = await this.onStartLevel(levelId);
    if (!ok) {
      this.setState(UIState.LEVEL_SELECT);
    }
  }

  showLevelComplete(levelId, timeMs) {
    this.currentLevelId = levelId;
    this.setState(UIState.WIN);
    this.winOverlay.show(levelId, timeMs);
  }

  showError(message) {
    alert(message);
  }

  showIdleHint() {
    if (this.state === UIState.PLAYING) this.hud.showIdleHint();
  }

  hideIdleHint() {
    this.hud.hideIdleHint();
  }

  _startTicker() {
    this._stopTicker();
    const tick = () => {
      if (this.state !== UIState.PLAYING && this.state !== UIState.WIN) return;
      this.hud.updateTime(this.getElapsedMs());
      this._tickHandle = requestAnimationFrame(tick);
    };
    this._tickHandle = requestAnimationFrame(tick);
  }

  _stopTicker() {
    if (this._tickHandle != null) cancelAnimationFrame(this._tickHandle);
    this._tickHandle = null;
  }
}
